import React, { useRef, useState, useEffect } from 'react'
import classnames from 'classnames'
import { JumpLink } from 'marketing-site/src/library/elements/JumpLink'
import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import { ColorName, getColorTheme, mq, Color } from '../../utils'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps } from './index'
import styles from './styles.scss'
import checkmarkwhite from '../../images/checkmark-white.svg'

export const FeatureSpotlightBase = (props: IProps) => {
  const [wrap, setWrap] = useState<HTMLDivElement | null>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  useEffect(() => {
    setWrap(wrapper.current)
  }, [])

  const {
    isZippered,
    isFlipped,
    eyebrow,
    heading,
    subheading,
    icon,
    iconText,
    bgColor,
    cta,
    children,
    renderMedia,
    jumpLink,
  } = props

  const colorTheme = getColorTheme('default', bgColor || Color.White)

  const isCTAValid = typeof cta?.() !== 'undefined'

  const spacing = useSpacing()

  const featureSpotlightClassnames = classnames('feature-spotlight', {
    'feature-spotlight--has-children': isCTAValid || children,
    [`feature-spotlight--${ColorName[colorTheme.id as Color]}`]: bgColor,
    'feature-spotlight--white-checkmarks': colorTheme.textColor === Color.White,
    'feature-spotlight--padding': !spacing,
  })

  const backgroundClassnames = classnames('feature-spotlight__background')

  const leadClassnames = classnames('feature-spotlight__lead', {
    'feature-spotlight__lead--flipped': isFlipped,
  })

  const mediaClassnames = classnames('feature-spotlight__media', {
    'feature-spotlight__media--flipped': isFlipped,
  })

  const childrenClassnames = classnames('feature-spotlight__children', {
    'feature-spotlight__children--zippered': isZippered,
    'feature-spotlight__children--flipped': isFlipped,
  })

  return (
    <div className={featureSpotlightClassnames} ref={wrapper}>
      {jumpLink && wrap && <JumpLink {...jumpLink} container={wrap} />}
      <div className={backgroundClassnames}>
        <div className={leadClassnames}>
          {icon && (
            <div className="feature-spotlight__icon-wrapper icon__wrapper">
              <img alt="Icon" src={icon} className="feature-spotlight__icon" />
              {iconText && (
                <div className="feature-spotlight__icon-text">
                  {iconText && (
                    <Text size="eyebrows" brand="default">
                      {iconText}
                    </Text>
                  )}
                </div>
              )}
            </div>
          )}

          {eyebrow && (
            <span className="feature-spotlight__eyebrow">
              <Text size="eyebrows" brand="default">
                {eyebrow}
              </Text>
            </span>
          )}

          <h2 className="feature-spotlight__heading">
            <Text size="heading-3" brand="default">
              {heading}
            </Text>
          </h2>

          {subheading && (
            <div className="feature-spotlight__subheading">
              <Text size="heading-4" brand="default">
                <RichText html={subheading} />
              </Text>
            </div>
          )}
        </div>

        {(isCTAValid || children) && (
          <div className={childrenClassnames}>
            {isCTAValid && <div className="feature-spotlight__cta">{cta?.()}</div>}
            {children}
          </div>
        )}

        <div className={mediaClassnames}>{renderMedia()}</div>
      </div>
      <style jsx>{styles}</style>
      <style jsx>{`
        .feature-spotlight {
          background-color: ${bgColor ? colorTheme.id : 'transparent'};
          color: ${colorTheme.textColor};
          &__background {
            display: grid;
            grid-template-columns: repeat(14, 1fr);
            grid-column: 1 / span 14;
            align-items: center;
            padding-left: 0;
            padding-right: 0;
            position: relative;
            width: 100%;
            background-color: inherit;

            &::before,
            &::after {
              content: '';
              position: absolute;
              background-color: inherit;
              left: 50%;
              mask-repeat: repeat no-repeat;
              width: 106vw;
              z-index: -1;
              transform: translateX(-50%);
            }
          }
        }
        @media (${mq.tablet}) {
          .feature-spotlight {
            &__background {
              grid-column: content;
              padding-left: 0;
              padding-right: 0;
              max-width: $container-max-width;
              margin: 0 auto;
              position: relative;
              grid-template-columns: repeat(12, 1fr);
            }
          }
        }
      `}</style>
      <style jsx global>{`
        .feature-spotlight {
          &__heading span {
            font-weight: 700;
          }
          &--white-checkmarks ul li::before {
            background-image: url(${checkmarkwhite});
          }
          &__media {
            video {
              border-radius: 25px;
              overflow: hidden;
            }
          }
          .feature-spotlight__lead > div > span > div > p {
            line-height: 144%;
          }
        }
        @media (${mq.mobile}) {
          .feature-spotlight {
            padding-left: 0;
            padding-right: 0;

            &--padding {
              padding-top: 1.75rem;
              padding-bottom: 1.75rem;
            }

            .feature-spotlight__subheading {
              margin-top: 1.25rem;
            }
            div.feature-spotlight__lead + div.feature-spotlight__children {
              margin-top: 2rem;
            }
          }
          .rich-text {
            ul li {
              display: flex;
              justify-content: center;
            }
          }
        }
        @media (${mq.tablet}) {
          .feature-spotlight {
            padding-left: 0;
            padding-right: 0;

            &--padding {
              padding-top: 6.25rem;
              padding-bottom: 6.25rem;
            }

            .feature-spotlight__heading + .feature-spotlight__subheading {
              margin-top: 1.5rem;
            }
            .feature-spotlight__heading span {
              font-size: 2.375rem;
            }
            .feature-spotlight__lead .feature-spotlight__eyebrow {
              margin-bottom: 1rem;
            }

            div.feature-spotlight__lead + div.feature-spotlight__children {
              margin-top: 2.5rem;
            }
          }
        }
      `}</style>
    </div>
  )
}
